import type { SortingState } from "@tanstack/react-table";

import { isTradeOpen } from "../../../../utils/positionSemantics";
import type { TradeLogTableRow } from "./tradeLogTradesTableTypes";

function compareTradeLogRows(
  a: TradeLogTableRow,
  b: TradeLogTableRow,
  columnId: string,
  desc: boolean,
  brokerOpenTradeIds?: ReadonlySet<string>,
): number {
  let aValue: string | number;
  let bValue: string | number;

  switch (columnId) {
    case "entryDate":
      aValue = new Date(a.entryDate).getTime();
      bValue = new Date(b.entryDate).getTime();
      break;
    case "exitDate":
      aValue = a.exitDate ? new Date(a.exitDate).getTime() : 0;
      bValue = b.exitDate ? new Date(b.exitDate).getTime() : 0;
      break;
    case "symbol":
      aValue = a.symbol.toLowerCase();
      bValue = b.symbol.toLowerCase();
      break;
    case "entryPrice":
      aValue = a.entryPrice;
      bValue = b.entryPrice;
      break;
    case "quantity":
      aValue = a.entryQuantity;
      bValue = b.entryQuantity;
      break;
    case "netPnL": {
      const aPnl = isTradeOpen(a, brokerOpenTradeIds) ? a.floatingPnl : a.netPnL;
      const bPnl = isTradeOpen(b, brokerOpenTradeIds) ? b.floatingPnl : b.netPnL;
      aValue = typeof aPnl === "number" && Number.isFinite(aPnl) ? aPnl : 0;
      bValue = typeof bPnl === "number" && Number.isFinite(bPnl) ? bPnl : 0;
      break;
    }
    case "returnPercent":
      aValue =
        typeof a.returnPercent === "number" && Number.isFinite(a.returnPercent)
          ? a.returnPercent
          : 0;
      bValue =
        typeof b.returnPercent === "number" && Number.isFinite(b.returnPercent)
          ? b.returnPercent
          : 0;
      break;
    default:
      return 0;
  }

  if (aValue < bValue) return desc ? 1 : -1;
  if (aValue > bValue) return desc ? -1 : 1;
  return 0;
}

/**
 * Open trades always appear first; closed rows sort by the active column below them.
 */
export function sortTradeLogRowsOpenFirst<T extends TradeLogTableRow>(
  rows: T[],
  sorting: SortingState,
  brokerOpenTradeIds?: ReadonlySet<string>,
): T[] {
  const open = rows.filter((row) => isTradeOpen(row, brokerOpenTradeIds));
  const closed = rows.filter((row) => !isTradeOpen(row, brokerOpenTradeIds));
  const sortState = sorting[0];

  if (!sortState) {
    const byExitDesc = (a: T, b: T) => {
      const aExit = a.exitDate ? new Date(a.exitDate).getTime() : 0;
      const bExit = b.exitDate ? new Date(b.exitDate).getTime() : 0;
      if (aExit !== bExit) return bExit - aExit;
      return new Date(b.entryDate).getTime() - new Date(a.entryDate).getTime();
    };
    return [...open.sort(byExitDesc), ...closed.sort(byExitDesc)];
  }

  const compare = (a: T, b: T) =>
    compareTradeLogRows(a, b, sortState.id, sortState.desc, brokerOpenTradeIds);

  return [...open.sort(compare), ...closed.sort(compare)];
}
